import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Card, Flex, Heading, Text } from '@radix-ui/themes'
import { formatReading, isCorrection } from '@willie/installation-guides'
import { useAuth } from '~/auth/AuthProvider'
import { ImageLightbox, openOnPlainClick } from '~/components/ImageLightbox'
import { formatTimestamp } from '~/utils/date'
import {
  getSubmittedIndexReading,
  listCaptureVerdicts,
  listInstallationCaptures,
  type AttachmentKind,
  type CaptureGateVerdict,
  type CaptureVerdict,
  type InstallationCapture
} from '~/features/installations/service'
import { PhotoLightboxFooter } from './PhotoLightboxFooter'
import { ShareImageLinkDialog } from './ShareImageLinkDialog'

// What each photo *is*, which the storage-listing gallery could never say.
const KIND_LABEL: Record<AttachmentKind, string> = {
  meter_index: 'Meter dial',
  module_id: 'Module ID',
  wide_shot: 'Whole installation',
  step_photo: 'Step photo'
}

/**
 * What the gate concluded. Gray for `unavailable` on purpose: a gate that could not run says nothing
 * about the photo, and colouring it as a failure would read as one.
 */
const VERDICT_CHROME: Record<CaptureVerdict, { label: string; color: 'green' | 'orange' | 'blue' | 'gray' }> = {
  pass: { label: 'Gate passed', color: 'green' },
  retake: { label: 'Retake asked', color: 'orange' },
  confirm: { label: 'Read, unconfirmed', color: 'blue' },
  unavailable: { label: 'Gate did not run', color: 'gray' }
}

const GateVerdictBadge = ({ verdict }: { verdict: CaptureGateVerdict }) => {
  const chrome = VERDICT_CHROME[verdict.verdict]
  const unmet = verdict.assertions.filter((assertion) => !assertion.met).map((assertion) => assertion.id)

  return (
    <Badge
      color={chrome.color}
      variant="soft"
      size="1"
      title={[
        verdict.unavailableReason ? `Reason: ${verdict.unavailableReason}` : null,
        unmet.length > 0 ? `Unmet: ${unmet.join(', ')}` : null,
        verdict.instruction,
        `Prompt ${verdict.promptVersion} · evaluation ${verdict.attempt}`
      ]
        .filter(Boolean)
        .join('\n')}
    >
      {chrome.label}
    </Badge>
  )
}

/**
 * Which arm the evaluation ran under.
 *
 * Beside the verdict rather than inside its tooltip: a title attribute is unreachable on the touch
 * devices this console is read on, and whether the installer was ever asked is the first question a
 * verdict raises.
 */
const GateArmBadge = ({ enforced }: { enforced: boolean }) => (
  <Badge color={enforced ? 'purple' : 'gray'} variant="outline" size="1">
    {enforced ? 'Enforced' : 'Shadow'}
  </Badge>
)

/**
 * What a person made of the model's transcription.
 *
 * Both values go through `formatReading`, so the console cannot disagree with the handset about where
 * the decimal point falls — which is the failure the two-observation reading shape exists to prevent.
 */
const ConfirmedReadingLine = ({ verdict }: { verdict: CaptureGateVerdict }) => {
  const confirmed = verdict.confirmedReadings[0]
  if (!confirmed || verdict.confirmedAt === null) return null

  const transcribed = verdict.readings.find((reading) => reading.id === confirmed.id)
  const model =
    transcribed && transcribed.raw !== null && transcribed.decimalDigits !== null
      ? { raw: transcribed.raw, decimalDigits: transcribed.decimalDigits }
      : null
  const corrected = isCorrection(confirmed, model)

  return (
    <Flex direction="column" gap="1">
      <Flex align="center" gap="2">
        <Text size="4" weight="bold">
          {formatReading(confirmed)}
        </Text>
        {/* A confirmation on a retake is the manual-entry path: the photo was refused and the
            installer typed the index anyway, which is the only reading that dial will ever have. */}
        <Badge color={corrected ? 'orange' : 'green'} variant="soft" size="1">
          {verdict.verdict === 'retake'
            ? 'Typed after a refused photo'
            : corrected
              ? 'Corrected by the installer'
              : 'Confirmed by the installer'}
        </Badge>
      </Flex>
      {corrected && model ? (
        <Text size="1" color="gray">
          Model read {formatReading(model)}
        </Text>
      ) : null}
      <Text size="1" color="gray">
        Confirmed {formatTimestamp(verdict.confirmedAt)}
      </Text>
    </Flex>
  )
}

interface CaptureCardProps {
  capture: InstallationCapture
  /** Rendered beside the meter dial so the pair can be checked without opening the image. */
  aside?: React.ReactNode
  verdict?: CaptureGateVerdict
  onOpen: () => void
}

const CaptureCard = ({ capture, aside, verdict, onOpen }: CaptureCardProps) => {
  const [errored, setErrored] = useState(false)

  return (
    <Flex direction="column" gap="2" className="rounded-[var(--radius-3)] border border-[color:var(--gray-5)] p-3">
      <Flex align="center" justify="between" gap="2" wrap="wrap">
        <Flex align="center" gap="2">
          <Text size="2" weight="medium">
            {capture.stepKey ? `${KIND_LABEL[capture.kind]} · ${capture.stepKey}` : KIND_LABEL[capture.kind]}
          </Text>
          {capture.retakeCount > 0 ? (
            <Badge color="gray" variant="soft" size="1" title="Earlier attempts are kept, not overwritten">
              {capture.retakeCount === 1 ? '1 retake' : `${capture.retakeCount} retakes`}
            </Badge>
          ) : null}
          {verdict ? (
            <>
              <GateVerdictBadge verdict={verdict} />
              <GateArmBadge enforced={verdict.enforced} />
            </>
          ) : null}
        </Flex>
        <ShareImageLinkDialog
          path={capture.path}
          trigger={
            <Text size="1" color="gray" className="cursor-pointer underline">
              Share link
            </Text>
          }
        />
      </Flex>

      <Flex gap="3" align="start" wrap="wrap">
        <Box className="w-40 shrink-0 overflow-hidden rounded-[var(--radius-2)] border border-[color:var(--gray-5)] bg-[var(--gray-2)]">
          {errored || !capture.url ? (
            <Flex align="center" justify="center" className="aspect-square w-full">
              <Text size="1" color="gray">
                Failed to load
              </Text>
            </Flex>
          ) : (
            <a
              href={capture.url}
              target="_blank"
              rel="noreferrer"
              className="block"
              onClick={openOnPlainClick(onOpen)}
            >
              <img
                src={capture.url}
                alt={KIND_LABEL[capture.kind]}
                className="aspect-square w-full object-cover"
                onError={() => setErrored(true)}
              />
            </a>
          )}
        </Box>

        <Flex direction="column" gap="1" className="min-w-0 flex-1">
          {verdict ? <ConfirmedReadingLine verdict={verdict} /> : null}
          {aside}
          <Text size="1" color="gray">
            Received {formatTimestamp(capture.receivedAt)}
          </Text>
          {capture.submittedBy ? (
            <Text size="1" color="gray">
              {capture.submittedBy}
            </Text>
          ) : null}
          {/* Shown separately, and only when it disagrees: the device clock is untrusted, and a wide
              gap is usually a phone with the wrong time rather than a late upload. */}
          {capture.capturedAt && capture.capturedAt.slice(0, 10) !== capture.receivedAt.slice(0, 10) ? (
            <Text size="1" color="amber">
              Device clock said {formatTimestamp(capture.capturedAt)}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  )
}

interface InstallationCapturesProps {
  installationId: number
  waterStationId: string | null
}

export const InstallationCaptures = ({ installationId, waterStationId }: InstallationCapturesProps) => {
  const { supabaseClient } = useAuth()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const capturesQuery = useQuery({
    queryKey: ['installation-captures', installationId] as const,
    queryFn: () => listInstallationCaptures(supabaseClient, installationId)
  })

  // Only once there is something to badge. Most installations predate the assistant and render
  // nothing, and this table is the one that grows with every re-run and prompt sweep.
  const verdictsQuery = useQuery({
    queryKey: ['installation-capture-verdicts', installationId] as const,
    queryFn: () => listCaptureVerdicts(supabaseClient, installationId),
    enabled: (capturesQuery.data ?? []).length > 0
  })

  const indexQuery = useQuery({
    queryKey: ['installation-submitted-index', waterStationId] as const,
    queryFn: () => getSubmittedIndexReading(supabaseClient, waterStationId),
    enabled: waterStationId !== null
  })

  const captures = capturesQuery.data ?? []
  const verdicts = verdictsQuery.data
  const reading = indexQuery.data ?? null
  const openCapture = openIndex === null ? null : (captures[openIndex] ?? null)

  // Nothing to say until the assistant has been used on this installation. The storage-listing
  // gallery below stays as-is for everything that predates it.
  if (!capturesQuery.isLoading && captures.length === 0 && !reading) return null

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between" gap="2">
          <Heading size="3">Captured in the app</Heading>
          {reading ? (
            <Badge color="green" variant="soft" size="1">
              Index submitted
            </Badge>
          ) : null}
        </Flex>

        {capturesQuery.isLoading ? (
          <Text size="2" color="gray">
            Loading…
          </Text>
        ) : capturesQuery.error ? (
          <Text size="2" color="red">
            Failed to load captures. Refresh the page to retry.
          </Text>
        ) : (
          <Flex direction="column" gap="2">
            {captures.map((capture, index) => (
              <CaptureCard
                key={capture.id}
                capture={capture}
                verdict={verdicts?.get(capture.id)}
                onOpen={() => setOpenIndex(index)}
                aside={
                  capture.kind === 'meter_index' && reading ? (
                    <Flex direction="column" gap="1">
                      <Text size="4" weight="bold">
                        {reading.value}
                      </Text>
                      <Text size="1" color="gray">
                        Index · read {formatTimestamp(reading.readingAt)}
                      </Text>
                    </Flex>
                  ) : null
                }
              />
            ))}

            {/* An index with no dial photo is worth flagging: it is the pair that makes either
                half checkable. */}
            {reading && !captures.some((capture) => capture.kind === 'meter_index') ? (
              <Flex direction="column" gap="1" className="rounded-[var(--radius-3)] border border-[color:var(--gray-5)] p-3">
                <Text size="4" weight="bold">
                  {reading.value}
                </Text>
                <Text size="1" color="gray">
                  Index · read {formatTimestamp(reading.readingAt)} · no meter-dial photo
                </Text>
              </Flex>
            ) : null}
          </Flex>
        )}
      </Flex>

      <ImageLightbox
        images={captures.map((capture) => capture.url)}
        openIndex={openIndex}
        onOpenChange={setOpenIndex}
        footer={openCapture ? <PhotoLightboxFooter sharePath={openCapture.path} /> : null}
      />
    </Card>
  )
}
